import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, 
  Table, TableBody, TableCell, TableHead, TableRow, Avatar,
  Dialog, DialogTitle, DialogContent, IconButton, Collapse, Button, DialogActions
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import TradeFormFields from '../trades/TradeFormFields';
import { calculateTradeMetrics } from '../../utils/tradeUtils';

const WatchlistSymbolPerformance = ({ trades = [], onTradeUpdate, strategies = [] }) => {
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedSymbol, setExpandedSymbol] = useState(null);
  const [editTradeOpen, setEditTradeOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);

  // Format risk-reward ratio as 1:X
  const formatRiskRewardRatio = (ratio) => {
    if (!ratio || ratio <= 0 || typeof ratio !== 'number') return '1:1';
    return `1:${ratio.toFixed(1)}`.replace('.0', '');
  };

  const generateInsights = (tradesData) => {
    const symbolStats = {};
    
    tradesData.forEach(trade => {
      const symbol = trade.symbol;
      if (!symbol) return;
      
      if (!symbolStats[symbol]) {
        symbolStats[symbol] = {
          symbol,
          totalTrades: 0,
          wins: 0,
          losses: 0,
          totalPnL: 0,
          avgRating: 0,
          categories: new Set(),
          exchanges: new Set(),
          lastTradeDate: null,
          avgRiskReward: 0,
          riskRewardSum: 0,
          ratingSum: 0
        };
      }
      
      const stats = symbolStats[symbol];
      stats.totalTrades++;
      stats.categories.add(trade.category);
      if (trade.exchange) stats.exchanges.add(trade.exchange);
      
      if (trade.status === 'WIN') stats.wins++;
      if (trade.status === 'LOSS') stats.losses++;
      
      const pnl = parseFloat(trade.pnl || trade.profitLoss || 0);
      if (!isNaN(pnl)) stats.totalPnL += pnl;
      
      const rating = parseFloat(trade.rating || 0);
      if (rating > 0) stats.ratingSum += rating;
      
      const rr = parseFloat(trade.riskRewardRatio || 0);
      if (rr > 0) stats.riskRewardSum += rr;
      
      if (!stats.lastTradeDate || new Date(trade.tradeDate) > new Date(stats.lastTradeDate)) {
        stats.lastTradeDate = trade.tradeDate;
      }
    });
    
    return Object.values(symbolStats).map(stats => {
      const rawRiskReward = stats.totalTrades > 0 ? (stats.riskRewardSum / stats.totalTrades) : 0;
      return {
        ...stats,
        winRate: stats.totalTrades > 0 ? ((stats.wins / stats.totalTrades) * 100).toFixed(1) : 0,
        avgRating: stats.totalTrades > 0 ? (stats.ratingSum / stats.totalTrades).toFixed(1) : 0,
        avgRiskReward: stats.totalTrades > 0 ? (stats.riskRewardSum / stats.totalTrades).toFixed(2) : 0,
        avgRiskRewardFormatted: formatRiskRewardRatio(rawRiskReward),
        categories: Array.from(stats.categories),
        exchanges: Array.from(stats.exchanges)
      };
    }).sort((a, b) => b.totalTrades - a.totalTrades);
  };

  const symbolInsights = generateInsights(trades);

  const getPerformanceColor = (pnl) => {
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#ff9800';
  };

  const getWinRateColor = (winRate) => {
    if (winRate >= 70) return '#4caf50';
    if (winRate >= 50) return '#ff9800';
    return '#f44336';
  };

  const handleEditTrade = (trade) => {
    setEditingTrade({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
    setEditTradeOpen(true);
  };

  const handleTradeChange = (field, value) => {
    setEditingTrade(prev => {
      const updated = { ...prev, [field]: value };
      
      // Only auto-calculate P&L if manual override is not enabled
      if (!updated.manualPnL && field === 'status') {
        const reward = parseFloat(updated.rewardAmount);
        const risk = parseFloat(updated.riskAmount);
        const status = value;
        
        if (!isNaN(reward) && !isNaN(risk)) {
          const pnl =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' : '';
          updated.profitLoss = pnl;
          updated.pnl = pnl;
        }
      }
      
      return updated;
    });
  };

  const handleSaveTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const metrics = calculateTradeMetrics(
        editingTrade.entryPrice,
        editingTrade.stopLoss,
        editingTrade.exitPrice,
        editingTrade.positionSize,
        editingTrade.leverage
      );
      
      // Calculate profit/loss based on status
      if (!editingTrade.manualPnL) {
        const reward = parseFloat(metrics.rewardAmount);
        const risk = parseFloat(metrics.riskAmount);
        const status = editingTrade.status;

        if (!isNaN(reward) && !isNaN(risk)) {
          editingTrade.profitLoss =
            status === 'WIN' ? reward.toFixed(2) :
            status === 'LOSS' ? `-${risk.toFixed(2)}` :
            status === 'BREAKEVEN' ? '0' :
            '';
          editingTrade.pnl = editingTrade.profitLoss;
        }
      }
      
      const payload = {
        ...editingTrade,
        ...metrics,
        rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatioFormatted || '1:1'} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
      };
      
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditTradeOpen(false);
      setEditingTrade(null);
      if (onTradeUpdate) await onTradeUpdate();
    } catch (err) {
      console.error('Error updating trade:', err);
    }
  };

  const handleDeleteTrade = async () => {
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/deleteTrade', {
        tradeId: editingTrade.tradeId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditTradeOpen(false);
      setEditingTrade(null);
      if (onTradeUpdate) await onTradeUpdate();
    } catch (err) {
      console.error('Error deleting trade:', err);
    }
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Symbols</Typography>
              <Typography variant="h3" color="primary">{symbolInsights.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Most Traded</Typography>
              <Typography variant="h5">{symbolInsights[0]?.symbol || 'N/A'}</Typography>
              <Typography variant="body2">{symbolInsights[0]?.totalTrades || 0} trades</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Best Performer</Typography>
              <Typography variant="h5">
                {symbolInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.symbol || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#4caf50' }}>
                ${symbolInsights.sort((a, b) => b.totalPnL - a.totalPnL)[0]?.totalPnL.toFixed(2) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Symbol Performance</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell>Trades</TableCell>
              <TableCell>Win Rate</TableCell>
              <TableCell>P&L</TableCell>
              <TableCell>Avg R:R</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {symbolInsights.slice(0, 10).map((insight) => (
              <React.Fragment key={insight.symbol}>
                <TableRow hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => setExpandedSymbol(expandedSymbol === insight.symbol ? null : insight.symbol)}
                      >
                        {expandedSymbol === insight.symbol ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {insight.symbol.charAt(0)}
                      </Avatar>
                      <Typography variant="subtitle2">
                        {insight.symbol}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={insight.totalTrades} 
                      size="small" 
                      icon={<ShowChartIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${insight.winRate}%`}
                      size="small"
                      sx={{ bgcolor: getWinRateColor(insight.winRate), color: 'white' }}
                      icon={insight.winRate >= 50 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ color: getPerformanceColor(insight.totalPnL), fontWeight: 'bold' }}
                    >
                      ${insight.totalPnL.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>{insight.avgRiskRewardFormatted || '1:1'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${insight.avgRating}/5`} 
                      size="small" 
                      color={insight.avgRating >= 4 ? 'success' : insight.avgRating >= 3 ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    {insight.categories.map(cat => (
                      <Chip key={cat} label={cat} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="View Details" 
                      size="small" 
                      color="primary" 
                      onClick={() => {
                        setSelectedSymbol(insight.symbol);
                        setDetailsOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                </TableRow>
                
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={expandedSymbol === insight.symbol} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Recent Trades for {insight.symbol}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Entry</TableCell>
                              <TableCell>Exit</TableCell>
                              <TableCell>P&L</TableCell>
                              <TableCell>Rating</TableCell>
                              <TableCell>Edit</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {trades
                              .filter(trade => trade.symbol === insight.symbol)
                              .slice(0, 5)
                              .map((trade) => (
                              <TableRow key={trade.tradeId}>
                                <TableCell>{trade.tradeDate}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={trade.status} 
                                    size="small" 
                                    color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>${trade.entryPrice}</TableCell>
                                <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                                <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red' }}>
                                  ${trade.profitLoss || '0'}
                                </TableCell>
                                <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                                <TableCell>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditTrade(trade)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Paper>
      
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedSymbol} - All Trades
          <IconButton onClick={() => setDetailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Entry Price</TableCell>
                <TableCell>Exit Price</TableCell>
                <TableCell>Stop Loss</TableCell>
                <TableCell>P&L</TableCell>
                <TableCell>R:R</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades
                .filter(trade => trade.symbol === selectedSymbol)
                .map((trade) => (
                <TableRow key={trade.tradeId}>
                  <TableCell>{trade.tradeDate}</TableCell>
                  <TableCell>
                    <Chip 
                      label={trade.status} 
                      size="small" 
                      color={trade.status === 'WIN' ? 'success' : trade.status === 'LOSS' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{trade.direction}</TableCell>
                  <TableCell>${trade.entryPrice}</TableCell>
                  <TableCell>${trade.exitPrice || 'N/A'}</TableCell>
                  <TableCell>${trade.stopLoss}</TableCell>
                  <TableCell sx={{ color: parseFloat(trade.profitLoss) >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                    ${trade.profitLoss || '0'}
                  </TableCell>
                  <TableCell>{trade.riskRewardRatioFormatted || (typeof trade.riskRewardRatio === 'number' ? formatRiskRewardRatio(trade.riskRewardRatio) : '1:1')}</TableCell>
                  <TableCell>{trade.rating || 'N/A'}/5</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setDetailsOpen(false);
                        handleEditTrade(trade);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
      
      <Dialog open={editTradeOpen} onClose={() => setEditTradeOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit Trade - {editingTrade?.symbol}
        </DialogTitle>
        <DialogContent sx={{ pt: 6, pb: 3 }}>
          {editingTrade && (
            <Box sx={{ mt: 2 }}>
              <TradeFormFields
                values={editingTrade}
                onChange={handleTradeChange}
                strategies={strategies}
                trades={trades}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setEditTradeOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTrade}
            sx={{ minWidth: 100 }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteTrade}
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 100 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WatchlistSymbolPerformance;